// BUILD-046 task 8.23 (supporting model): the issuer actions on an "Everyone must complete" request - add a recipient, remove a recipient,
// cancel the request (design "Group / Shared view and Take task": "lets permitted actors add a recipient, remove a recipient, or cancel";
// Req 5.9, 5.12, 5.13, 5.14, 5.18, 5.19, 5.20, 14.27; Decision D-J, item 3).
//
// Who is offered them (the recorded modify-set rule): the REQUESTER, the requester's DIRECT MANAGER and the COMPANY holder. Nobody else:
// not a recipient, not a Work_Group_Administrator by that authority alone, not a manager who is not the requester's direct manager. The Caller
// summary cannot say whose direct manager the Caller is, so the manager basis needs a fact the Caller summary does not carry
// (`requesterReportsToMe`); in S1 no route supplies it, so a manager is not offered the actions on the strength of `canOversee` alone (the server
// would refuse most such attempts). The server decides every request; this model only avoids offering what the recorded rule already excludes.
//
// What each action does, and how it is shown (never as completion; history is kept by the server):
//   add recipient    : one more Eligible_Employee gets an assignment of their own (the server refuses a duplicate or a 40th-plus recipient).
//   remove recipient : ONE recipient's assignment ENDS with outcome "removed". It is not completed and is not counted in N. An assignment
//                      already completed stays completed, so Remove is offered only for a live (open) assignment.
//   cancel request   : every OPEN assignment ENDS with outcome "cancelled" and the request reads "cancelled", never as success. Completed
//                      assignments stay completed.
// A source-governed requirement is refused by the server (SOURCE_GOVERNED); the refusal is shown with the server's message and nothing changes.
//
// Pure module apart from the injected api. Runtime imports: the list-state model (pure) and the candidate loader of taskActionsFlow (pure).
// Erasable TypeScript only (run by `node --experimental-strip-types`).

import { initialListState, isStaleOutcome } from './listStateModel';
import type { ListState } from './listStateModel';
import { describeActionFailure, loadCandidates } from './taskActionsFlow';
import type { Candidate, CandidatesApi, FlowFailure } from './taskActionsFlow';
import { personDisplay } from './displayNames';

// ───────────────────────── who is offered the actions ─────────────────────────

export type IssuerBasis = 'requester' | 'company' | 'manager' | 'none';

export interface IssuerMe {
  employee?: { employeeId?: string } | null;
  canOversee?: boolean;
  companyOversight?: boolean;
  /** Recorded for completeness: it grants NOTHING here (Req 14.20, 14.27). */
  canAdministerGroups?: boolean;
}

export interface IssuerFacts {
  /** True only when something established that the requester is a Direct_Report of the Caller. No S1 route supplies it. */
  requesterReportsToMe?: boolean;
}

export function issuerBasis(me: IssuerMe | null | undefined, requesterEmployeeId: string | null | undefined, facts: IssuerFacts = {}): IssuerBasis {
  const viewer = typeof me?.employee?.employeeId === 'string' ? me.employee.employeeId : '';
  if (viewer === '') return 'none';
  if (typeof requesterEmployeeId === 'string' && requesterEmployeeId !== '' && requesterEmployeeId === viewer) return 'requester';
  if (me?.companyOversight === true) return 'company';
  if (me?.canOversee === true && facts.requesterReportsToMe === true) return 'manager';
  return 'none';
}

export interface IssuerMember {
  employeeId: string;
  ended?: boolean;
  status?: string | null;
}

export interface IssuerEntry {
  requesterEmployeeId: string | null;
  requestState: 'issued' | 'cancelled' | null;
  members: readonly IssuerMember[];
}

export interface IssuerControls {
  basis: IssuerBasis;
  add: boolean;
  cancel: boolean;
  /** Employee ids whose live assignment may be removed. */
  removable: readonly string[];
}

const OPEN_STATUSES: readonly string[] = ['active', 'in_progress', 'waiting', 'blocked'];

const NO_CONTROLS: IssuerControls = Object.freeze({ basis: 'none' as IssuerBasis, add: false, cancel: false, removable: [] as string[] }) as IssuerControls;

/** A live assignment: not ended, and open (a completed assignment stays completed and is not removed). */
export function isLiveAssignment(member: IssuerMember): boolean {
  return member.ended !== true && typeof member.status === 'string' && OPEN_STATUSES.includes(member.status);
}

export function issuerControls(me: IssuerMe | null | undefined, entry: IssuerEntry | null | undefined, facts: IssuerFacts = {}): IssuerControls {
  if (entry === null || entry === undefined) return NO_CONTROLS;
  const basis = issuerBasis(me, entry.requesterEmployeeId, facts);
  // An unread set (state unknown) or a cancelled one offers nothing: the request is over, or its state is not known.
  if (basis === 'none' || entry.requestState !== 'issued') return NO_CONTROLS;
  const live = entry.members.filter(isLiveAssignment);
  return { basis, add: true, cancel: live.length > 0, removable: live.map((m) => m.employeeId) };
}

// ───────────────────────── the flow of one request ─────────────────────────

export type IssuerPhase = 'idle' | 'adding' | 'confirming-remove' | 'confirming-cancel' | 'sending';

export interface IssuerFlowState {
  phase: IssuerPhase;
  /** The person being added (chosen in the picker) or removed (named by the control); '' otherwise. */
  targetEmployeeId: string;
  /** The display name of the person being removed, kept for the confirmation and the outcome. */
  targetName: string;
  /** What the sending phase was started from, so a refusal returns to it. */
  from: Exclude<IssuerPhase, 'idle' | 'sending'> | null;
  candidates: ListState<Candidate>;
  failure: FlowFailure | null;
  outcome: string | null;
}

export function initialIssuerFlow(): IssuerFlowState {
  return { phase: 'idle', targetEmployeeId: '', targetName: '', from: null, candidates: initialListState<Candidate>(), failure: null, outcome: null };
}

/** A Caller change drops the open control, the choice and the candidate list. */
export function callerChangedIssuer(): IssuerFlowState {
  return initialIssuerFlow();
}

function guarded(state: IssuerFlowState): boolean {
  return state.phase === 'sending';
}

export function openAdd(state: IssuerFlowState): IssuerFlowState {
  if (guarded(state)) return state;
  return { ...state, phase: 'adding', targetEmployeeId: '', targetName: '', from: 'adding', failure: null, outcome: null };
}

export function selectRecipient(state: IssuerFlowState, employeeId: string): IssuerFlowState {
  if (state.phase !== 'adding') return state;
  return { ...state, targetEmployeeId: employeeId, failure: null };
}

export function askRemove(state: IssuerFlowState, employeeId: string, displayName: string): IssuerFlowState {
  if (guarded(state) || employeeId === '') return state;
  return { ...state, phase: 'confirming-remove', targetEmployeeId: employeeId, targetName: displayName, from: 'confirming-remove', failure: null, outcome: null };
}

export function askCancel(state: IssuerFlowState): IssuerFlowState {
  if (guarded(state)) return state;
  return { ...state, phase: 'confirming-cancel', targetEmployeeId: '', targetName: '', from: 'confirming-cancel', failure: null, outcome: null };
}

export function closeIssuer(state: IssuerFlowState): IssuerFlowState {
  if (state.phase === 'idle' || guarded(state)) return state;
  return { ...state, phase: 'idle', targetEmployeeId: '', targetName: '', from: null, failure: null };
}

export function dismissIssuerOutcome(state: IssuerFlowState): IssuerFlowState {
  return state.outcome === null ? state : { ...state, outcome: null };
}

export type IssuerRequest = { kind: 'add'; requestId: string; employeeId: string } | { kind: 'remove'; requestId: string; employeeId: string; name: string } | { kind: 'cancel'; requestId: string };

/** The confirm click. `send` is non-null exactly when the request must now be sent; a second click while it runs sends nothing. */
export function issuerConfirm(state: IssuerFlowState, ctx: { requestId: string }): { state: IssuerFlowState; send: IssuerRequest | null } {
  if (guarded(state)) return { state, send: null };
  let send: IssuerRequest | null = null;
  if (state.phase === 'adding' && state.targetEmployeeId !== '') send = { kind: 'add', requestId: ctx.requestId, employeeId: state.targetEmployeeId };
  else if (state.phase === 'confirming-remove' && state.targetEmployeeId !== '') send = { kind: 'remove', requestId: ctx.requestId, employeeId: state.targetEmployeeId, name: state.targetName };
  else if (state.phase === 'confirming-cancel') send = { kind: 'cancel', requestId: ctx.requestId };
  if (send === null) return { state, send: null };
  return { state: { ...state, phase: 'sending', failure: null }, send };
}

export interface IssuerResponse {
  ended?: unknown;
  preserved?: unknown;
  added?: { employeeId?: unknown } | null;
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

/** What the accepted action did, in words. A removal or a cancellation is an ENDING, never a completion. */
export function issuerOutcomeText(state: IssuerFlowState, request: IssuerRequest, response: IssuerResponse): string {
  switch (request.kind) {
    case 'add': {
      const found = state.candidates.items.find((c) => c.employeeId === request.employeeId);
      // D1 display cleanup: First name + last initial, no "(FICTIONAL)" test marker (the stored name is not changed).
      const name = found !== undefined && personDisplay(found.displayName, { department: found.department }).short !== '' ? personDisplay(found.displayName, { department: found.department }).short : 'The person you chose';
      return `${name} was added with an assignment of their own.`;
    }
    case 'remove': {
      const removed = personDisplay(request.name).short;
      return `${removed === '' ? 'The recipient' : removed} was removed. Their assignment ended; it is not counted as completed.`;
    }
    case 'cancel': {
      const ended = count(response.ended);
      const kept = count(response.preserved);
      const open = `${ended} open assignment${ended === 1 ? '' : 's'} ended (not counted as completed)`;
      const stayed = kept > 0 ? `; ${kept} already completed assignment${kept === 1 ? '' : 's'} stayed completed` : '';
      return `The request was cancelled. ${open}${stayed}.`;
    }
  }
}

export function issuerSucceeded(state: IssuerFlowState, request: IssuerRequest, response: IssuerResponse): IssuerFlowState {
  if (state.phase !== 'sending') return state;
  return { ...state, phase: 'idle', targetEmployeeId: '', targetName: '', from: null, failure: null, outcome: issuerOutcomeText(state, request, response) };
}

/** A refusal (or no answer): the same control stays open with the same choice, and nothing on the screen changes. */
export function issuerFailed(state: IssuerFlowState, thrown: unknown): IssuerFlowState {
  if (state.phase !== 'sending' || state.from === null) return state;
  if (isStaleOutcome(thrown)) return state;
  return { ...state, phase: state.from, failure: describeActionFailure(thrown) };
}

// ───────────────────────── the flow the component runs ─────────────────────────

export interface IssuerApi extends CandidatesApi {
  addSetRecipient: (requestId: string, employeeId: string) => Promise<Record<string, unknown>>;
  removeSetRecipient: (requestId: string, employeeId: string) => Promise<Record<string, unknown>>;
  cancelSet: (requestId: string) => Promise<Record<string, unknown>>;
}

export interface IssuerStore {
  get: () => IssuerFlowState;
  set: (next: IssuerFlowState) => void;
}

export type IssuerOutcome = { kind: 'ignored' } | { kind: 'done'; request: IssuerRequest; response: Record<string, unknown> } | { kind: 'failed' } | { kind: 'dropped' };

/** Loads (or reloads, for Retry) the people the picker offers. */
export function loadRecipients(api: CandidatesApi, store: IssuerStore): Promise<void> {
  return loadCandidates(api, store);
}

function send(api: IssuerApi, request: IssuerRequest): Promise<Record<string, unknown>> {
  switch (request.kind) {
    case 'add':
      return api.addSetRecipient(request.requestId, request.employeeId);
    case 'remove':
      return api.removeSetRecipient(request.requestId, request.employeeId);
    case 'cancel':
      return api.cancelSet(request.requestId);
  }
}

/**
 * Runs the open control. The state changes before the request is sent; a repeat activation while it runs sends nothing. On `done` the caller
 * hands the answer to the issued store (which shows the updated figure) and fires `shared-tasks:changed`.
 */
export async function performIssuerAction(api: IssuerApi, store: IssuerStore, ctx: { requestId: string }): Promise<IssuerOutcome> {
  const step = issuerConfirm(store.get(), ctx);
  if (step.send === null) return { kind: 'ignored' };
  store.set(step.state);
  try {
    const response = await send(api, step.send);
    store.set(issuerSucceeded(store.get(), step.send, response));
    return { kind: 'done', request: step.send, response };
  } catch (thrown) {
    if (isStaleOutcome(thrown)) return { kind: 'dropped' };
    store.set(issuerFailed(store.get(), thrown));
    return { kind: 'failed' };
  }
}

// ───────────────────────── what the panel shows ─────────────────────────

export interface IssuerFlowView {
  phase: IssuerPhase;
  open: boolean;
  /** The people the picker offers: everyone not already in the set (a removed recipient's id stays taken: Req 5.12). */
  options: Candidate[];
  candidates: ListState<Candidate>;
  selected: string;
  prompt: string | null;
  confirm: { label: string; disabled: boolean };
  sending: boolean;
  failure: FlowFailure | null;
  outcome: string | null;
}

export function describeIssuerFlow(state: IssuerFlowState, ctx: { takenEmployeeIds: readonly string[] }): IssuerFlowView {
  const open = state.phase !== 'idle';
  const taken = new Set(ctx.takenEmployeeIds);
  let prompt: string | null = null;
  let label = '';
  if (state.from === 'adding' || state.phase === 'adding') {
    prompt = 'Add one more person to this Everyone request. They get a task of their own that they must complete. Work already done by the others stays as it is.';
    label = 'Add';
  } else if (state.from === 'confirming-remove' || state.phase === 'confirming-remove') {
    prompt = `Remove ${personDisplay(state.targetName).short === '' ? 'this recipient' : personDisplay(state.targetName).short}? Their assignment ends. It is not counted as completed.`;
    label = 'Remove';
  } else if (state.from === 'confirming-cancel' || state.phase === 'confirming-cancel') {
    prompt = 'Cancel this request? Every open assignment ends. Assignments already completed stay completed.';
    label = 'Cancel request';
  }
  return {
    phase: state.phase,
    open,
    options: state.candidates.phase === 'data' ? state.candidates.items.filter((c) => !taken.has(c.employeeId)) : [],
    candidates: state.candidates,
    selected: state.targetEmployeeId,
    prompt: open ? prompt : null,
    confirm: {
      label: state.phase === 'sending' ? 'Saving…' : label,
      disabled: state.phase === 'sending' || ((state.phase === 'adding') && state.targetEmployeeId === ''),
    },
    sending: state.phase === 'sending',
    failure: state.failure,
    outcome: state.outcome,
  };
}
